import React, { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useLocation } from "react-router-dom";
import { otpSchema } from "../../schemas/otpSchema";
import {
  useVerifyOTP,
  useResendOTP,
  useVerifyOTPNoAuth,
  useForgotPassword,
} from "../../custom-hooks/queries";
import { toast } from "../../components/common/Toast";
import { useAuthStore } from "../../store/useAuthStore";
import { FiLoader } from "react-icons/fi";

// NOTE: Make sure otpSchema ko bhi 5 digits ka bana dena!
const OTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";
  const flow = location.state?.flow || ""; // 'signup' or 'forgot-password'
  const role = useAuthStore((state) => state.role);
  const [otp, setOtp] = useState(["", "", "", "", ""]); // 🔥 5 digits now
  const inputRefs = useRef([]);
  const [countdown, setCountdown] = useState(30);
  const [loading, setLoading] = useState(false);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    register,
  } = useForm({
    resolver: yupResolver(otpSchema),
    defaultValues: {
      otp: "",
    },
  });

  const { mutate: verifyOTP } = useVerifyOTP();
  const { mutate: verifyOTPNoAuth } = useVerifyOTPNoAuth();
  const { mutate: resendOTP, isPending: isResending } = useResendOTP();
  const { mutate: forgotPassword, isPending } = useForgotPassword();

  const token = useAuthStore((state) => state.token);

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResendOTP = () => {
    forgotPassword(
      { email },
      {
        onSuccess: () => {
          toast.success("OTP resent successfully!");
          setCountdown(30);
        },
        onError: (error) => {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to resend OTP. Please try again.";
          toast.error(errorMessage);
        },
      },
    );
  };

  // Register OTP value
  register("otp");

  const handleChange = (index, value) => {
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    setValue("otp", newOtp.join(""), { shouldValidate: true });

    // Move to next box
    if (value && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").slice(0, 5);

    if (!/^\d+$/.test(pasted)) return;

    const newOtp = [...otp];
    for (let i = 0; i < 5; i++) {
      newOtp[i] = pasted[i] || "";
    }

    setOtp(newOtp);
    setValue("otp", newOtp.join(""), { shouldValidate: true });

    const lastIndex = Math.min(pasted.length - 1, 4);
    inputRefs.current[lastIndex]?.focus();
  };

  const onSubmit = (data) => {
    setLoading(true);

    const mutateFn = token ? verifyOTP : verifyOTPNoAuth;
    const bodyData = token
      ? { code: data.otp } // Include email for authenticated flow if needed
      : { code: data.otp, email }; // Include email for unauthenticated flow

    mutateFn(bodyData, {
      onSuccess: () => {
        setLoading(false);

        toast.success("OTP verified successfully");

        // Navigate based on flow type
        if (flow === "forgot-password") {
          // Forgot password flow - go to reset password
          navigate("/reset-password", { state: { email } });
        } else {
          // Signup flow - navigate based on role
          const normalizedRole = role?.toLowerCase();
          if (normalizedRole === "vendor") {
            navigate("/vendor/dashboard", { replace: true });
          } else if (normalizedRole === "admin") {
            navigate("/admin/dashboard", { replace: true });
          } else {
            navigate("/user/home", { replace: true });
          }
        }
      },
      onError: (error) => {
        setLoading(false);

        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to verify OTP. Please try again.";
        toast.error(errorMessage);
      },
    });
  };

  return (
    <div className="flex items-center justify-center px-4 py-6 sm:py-8">
      <div className="w-full max-w-[500px] border border-gray-200 shadow-sm rounded-2xl p-6 sm:p-10 bg-white">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-2">
          Enter OTP
        </h1>
        <p className="text-sm sm:text-base text-gray-600 text-center mb-4">
          We've sent a 5-digit code to your email. Please enter it below.
        </p>

        <div className="flex justify-center mb-6 sm:mb-8">
          <div className="w-[165px] h-[5px] bg-[#16BBD8] rounded"></div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div>
            <div className="flex justify-center gap-2 sm:gap-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className={`w-10 h-10 sm:w-12 sm:h-12 text-center text-lg sm:text-xl font-semibold border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1FADC6] transition-colors ${
                    errors.otp ? "border-red-500" : "border-gray-300"
                  }`}
                />
              ))}
            </div>

            {errors.otp && (
              <p className="mt-2 text-sm text-red-600 text-center">
                {errors.otp.message}
              </p>
            )}
          </div>
          <div className="text-center text-sm text-gray-600">
            {countdown > 0 && `Remaining time: ${countdown}s`}
          </div>

          {/* Resend OTP Button */}
          <div className="text-center flex justify-end gap-5 mt-10">
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={countdown > 0 || isPending}
              className={`text-sm font-medium transition-colors ${
                countdown > 0 || isPending
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-[#00C2C7] hover:text-[#1FADC6] cursor-pointer"
              }`}
            >
              Resend OTP
            </button>
            <button
              type="submit"
              className="w-[140px] flex items-center justify-center gap-2 bg-[#16BBD8] hover:bg-[#1FADC6] text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              {loading && <FiLoader className="animate-spin" />}
              <span>Verify OTP</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OTP;
